/**
 * DocumentsListPage — main browse + search surface for a Holder's
 * documents.
 *
 * Layout (post Phase 12d-3 cutover):
 *   - Storage usage bar at the top
 *   - Uploader (drag-and-drop)
 *   - Search bar — semantic search across the corpus, folder-agnostic
 *   - Left rail: folder tree (assembled client-side from the flat list)
 *               + a virtual "Trash" node at the bottom
 *   - Main panel: filtered document list for the selected folder, or
 *               soft-deleted rows when Trash is selected
 *
 * Protected folders (`is_protected = true`, the `/My-Fias/*` set) get
 * a lock badge; we don't expose rename/move/delete on them. User-
 * managed folders are read-only in this iteration — create/rename/
 * move actions are deferred to a follow-up since the substrate is in
 * place but the UX needs separate design.
 *
 * Sensitivity gating is server-enforced — the list never includes
 * docs above the caller's tier — but the UI surfaces a sensitivity
 * badge on each visible row so members understand what their team
 * mates can / can't see.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock,
  FolderOpen,
  Folder as FolderIcon,
  Trash2,
  Inbox,
  Plus,
  Pencil,
  X,
  Download as DownloadIcon,
  ArrowRightLeft,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  RotateCw,
  AlertCircle,
  Loader2,
  File as FileIcon,
  LayoutGrid,
  List as ListIcon,
  Info,
} from 'lucide-react';
import { api, ApiError, type VaultDocumentDto, type VaultDocumentFolderDto } from '@/lib/api';
import type { HolderSwitcherOption } from '@/components/HolderSwitcher';
import {
  useCreateVaultDocumentFolder,
  useDeleteVaultDocumentFolder,
  useEmptyVaultTrash,
  useEnsureVaultSystemFolders,
  useMoveVaultDocument,
  usePurgeVaultDocument,
  useRenameVaultDocumentFolder,
  useRestoreVaultDocument,
  useSoftDeleteVaultDocument,
  useVaultDocumentFolders,
  useVaultDocumentsList,
  useVaultDocumentsSearch,
} from '../hooks/useVaultDocuments';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SensitivityBadge } from '../components/SensitivityBadge';
import { DocumentUploader } from '../components/DocumentUploader';
import { VaultStorageUsage } from '@/components/files/VaultStorageUsage';

interface DocumentsListPageProps {
  holderId: string;
  /** Active Holder option from `useHolderSwitcher` — drives Holder-aware uploader defaults
   * (org → default 'confidential'; personal → hide sensitivity picker entirely). Optional
   * for backwards-compat with the arche-page route which doesn't need to thread it. */
  holder?: HolderSwitcherOption | null;
  /** Optional deep-link target. Canonical path like `/My-Fias/AI Images` —
   * the page resolves it to a folderId from the already-loaded folders
   * query and seeds the initial selection. Falls back to the root view
   * if the path doesn't match any folder in this Holder (e.g. Org Holder
   * that hasn't materialized the system scaffold yet). */
  initialFolderPath?: string;
  /** When true, suppress the inner h1/intro so the embedding page (e.g. MyDataPage) can
   * supply its own chrome without two stacked headers. */
  embedded?: boolean;
}

/** Sentinel for the virtual "everything at Holder root" node. */
const ROOT_SELECTION = '__root__';
/** Sentinel for the virtual Trash view (deleted_at IS NOT NULL). */
const TRASH_SELECTION = '__trash__';

type Selection = typeof ROOT_SELECTION | typeof TRASH_SELECTION | string; // string = folderId

export function DocumentsListPage({
  holderId,
  holder,
  initialFolderPath,
  embedded,
}: DocumentsListPageProps) {
  const navigate = useNavigate();
  const [selection, setSelection] = useState<Selection>(ROOT_SELECTION);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchSubmitted, setSearchSubmitted] = useState('');
  const [movingDoc, setMovingDoc] = useState<VaultDocumentDto | null>(null);
  const [purgingDoc, setPurgingDoc] = useState<VaultDocumentDto | null>(null);
  const [emptyTrashConfirmOpen, setEmptyTrashConfirmOpen] = useState(false);
  const [viewerDoc, setViewerDoc] = useState<VaultDocumentDto | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  // On narrow screens the two-panel layout (folder rail + files) is too
  // cramped to use; this toggle lets the user show only one at a time.
  // Above `md` both panels render side-by-side and the toggle is hidden.
  const [mobilePane, setMobilePane] = useState<'folders' | 'files'>('files');
  const softDelete = useSoftDeleteVaultDocument();
  const restore = useRestoreVaultDocument();
  const move = useMoveVaultDocument();

  const folders = useVaultDocumentFolders(holderId);

  // Self-heal for legacy Holders provisioned before the folder substrate
  // shipped (commit e80e6677, 2026-05-18). Those Holders skipped
  // `ensureSystemFolders` at creation time and have zero rows in
  // `vault_document_folders`, leaving the tree with only "All documents"
  // + "Trash" + no `/My-Fias/...` scaffold. The recovery route is
  // idempotent, so firing it once per Holder per mount when the folders
  // query lands empty patches the legacy state without affecting
  // already-scaffolded Holders. New Holders are unaffected (their
  // creation transaction provisions the scaffold).
  const ensureSystemFolders = useEnsureVaultSystemFolders();
  const ensureSystemFoldersMutate = ensureSystemFolders.mutate;
  const ensureAttempted = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!holderId) return;
    if (folders.isLoading || folders.isError) return;
    if ((folders.data ?? []).length > 0) return;
    if (ensureAttempted.current.has(holderId)) return;
    ensureAttempted.current.add(holderId);
    ensureSystemFoldersMutate(holderId);
  }, [holderId, folders.isLoading, folders.isError, folders.data, ensureSystemFoldersMutate]);

  // Resolve `initialFolderPath` → folderId from the already-loaded folders
  // list and seed the selection. One-shot per (holderId, path) pair so a
  // user who deep-links in and then navigates the tree manually doesn't get
  // snapped back. Gracefully no-ops if the path doesn't match a folder in
  // this Holder — the page just stays on the root view.
  const folderPathApplied = useRef<string | null>(null);
  useEffect(() => {
    if (!initialFolderPath) return;
    if (!holderId) return;
    if (folders.isLoading || folders.isError) return;
    const key = `${holderId}::${initialFolderPath}`;
    if (folderPathApplied.current === key) return;
    const match = (folders.data ?? []).find((f) => f.path === initialFolderPath);
    if (!match) return;
    folderPathApplied.current = key;
    setSelection(match.folderId);
  }, [initialFolderPath, holderId, folders.isLoading, folders.isError, folders.data]);

  // Filter the document list based on selection.
  // - root      → folderId: null
  // - <vdf_*>   → folderId: <id>
  // - trash     → deletedOnly: true (folder-agnostic; trash is a view, not a folder)
  const listFilters = useMemo(() => {
    if (selection === TRASH_SELECTION) {
      return { deletedOnly: true, includeArchived: true };
    }
    if (selection === ROOT_SELECTION) {
      return { folderId: null, includeArchived };
    }
    return { folderId: selection, includeArchived };
  }, [selection, includeArchived]);

  const list = useVaultDocumentsList(holderId, listFilters);
  const search = useVaultDocumentsSearch(
    holderId,
    { query: searchSubmitted, topK: 20 },
    { enabled: !!searchSubmitted },
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {!embedded && (
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold text-foreground">Documents</h1>
          <p className="text-sm text-muted-foreground">
            Upload, search, and manage documents for this workspace. Sensitivity controls who can
            see each doc; semantic search finds the right chunks across PDF, DOCX, and text files.
          </p>
        </div>
      )}

      <VaultStorageUsage holderId={holderId} />

      {selection !== TRASH_SELECTION &&
        (() => {
          const targetFolder =
            selection === ROOT_SELECTION
              ? null
              : (folders.data ?? []).find((f) => f.folderId === selection);
          const folderLabel = targetFolder
            ? (targetFolder.path ?? targetFolder.name)
            : 'All documents';
          return (
            <DocumentUploader
              holderId={holderId}
              defaultSensitivity={holder?.holderType === 'org' ? 'confidential' : 'standard'}
              hideSensitivityPicker={holder?.holderType === 'personal'}
              folderId={selection === ROOT_SELECTION ? null : selection}
              folderLabel={folderLabel}
            />
          );
        })()}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSearchSubmitted(searchInput.trim());
        }}
        className="flex items-center gap-2"
      >
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search documents semantically — e.g. 'renewal terms', 'NDA scope'"
          className="flex-1 rounded border border-border bg-card px-3 py-2 text-sm"
          maxLength={2000}
        />
        <button
          type="submit"
          disabled={!searchInput.trim()}
          className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
        >
          Search
        </button>
        {searchSubmitted && (
          <button
            type="button"
            className="text-sm text-muted-foreground underline"
            onClick={() => {
              setSearchInput('');
              setSearchSubmitted('');
            }}
          >
            Clear
          </button>
        )}
      </form>

      {searchSubmitted ? (
        <SearchResults
          query={searchSubmitted}
          loading={search.isLoading}
          matches={search.data}
          onOpen={(documentId) => navigate(`/a/arche_vault_documents/${documentId}`)}
        />
      ) : (
        <div className="flex flex-col gap-3 md:flex-row md:gap-4">
          <div
            role="tablist"
            aria-label="Switch between folders and files"
            className="flex w-full rounded-md border border-border bg-card p-0.5 text-sm md:hidden"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mobilePane === 'folders'}
              onClick={() => setMobilePane('folders')}
              className={
                'flex-1 rounded px-3 py-1.5 transition-colors ' +
                (mobilePane === 'folders'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground')
              }
            >
              Folders
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobilePane === 'files'}
              onClick={() => setMobilePane('files')}
              className={
                'flex-1 rounded px-3 py-1.5 transition-colors ' +
                (mobilePane === 'files'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground')
              }
            >
              Files
            </button>
          </div>

          <aside
            className={
              (mobilePane === 'folders' ? 'block' : 'hidden') +
              ' w-full md:block md:w-56 md:shrink-0'
            }
          >
            <FolderTree
              holderId={holderId}
              folders={folders.data ?? []}
              loading={folders.isLoading}
              selection={selection}
              onSelect={(next) => {
                setSelection(next);
                // On mobile the folder rail is a full-screen pane;
                // after the user picks one, swap to the files view so
                // they can see the result of their selection.
                setMobilePane('files');
              }}
              onDropDocument={(folderId, documentId) => {
                if (selection === TRASH_SELECTION) return;
                move.mutate({ documentId, targetFolderId: folderId });
              }}
            />
          </aside>

          <div
            className={
              (mobilePane === 'files' ? 'flex' : 'hidden') + ' flex-1 flex-col gap-3 md:flex'
            }
          >
            {(() => {
              if (selection === TRASH_SELECTION) {
                return (
                  <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 md:hidden">
                    <Trash2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm font-medium text-foreground">Trash</span>
                  </div>
                );
              }
              const currentFolder =
                selection !== ROOT_SELECTION
                  ? (folders.data ?? []).find((f) => f.folderId === selection)
                  : undefined;
              // Build breadcrumb segments from the folder's canonical path
              // (e.g. "/My-Fias/AI Images" → ["My-Fias", "AI Images"]).
              // Each segment resolves back to a folder by its cumulative
              // path so the user can jump to any ancestor.
              const segments: string[] = currentFolder?.path?.split('/').filter(Boolean) ?? [];
              const allFolders = folders.data ?? [];
              const crumbs = segments.map((segment, i) => {
                const cumulativePath = '/' + segments.slice(0, i + 1).join('/');
                const folder = allFolders.find((f) => f.path === cumulativePath);
                return {
                  label: segment,
                  folderId: folder?.folderId ?? null,
                  isLast: i === segments.length - 1,
                };
              });
              return (
                <nav
                  aria-label="Folder breadcrumbs"
                  className="flex items-center gap-1 overflow-x-auto rounded-md border border-border bg-muted/30 px-3 py-2 text-sm md:hidden"
                >
                  <button
                    type="button"
                    onClick={() => setSelection(ROOT_SELECTION)}
                    disabled={selection === ROOT_SELECTION}
                    className={
                      'flex items-center gap-1.5 rounded px-1.5 py-0.5 shrink-0 ' +
                      (selection === ROOT_SELECTION
                        ? 'text-foreground font-medium cursor-default'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground')
                    }
                  >
                    <Inbox className="h-4 w-4" />
                    <span>All documents</span>
                  </button>
                  {crumbs.map((c, i) => (
                    <span key={i} className="flex items-center gap-1 shrink-0">
                      <span className="text-muted-foreground/60">/</span>
                      {c.isLast || !c.folderId ? (
                        <span className="rounded px-1.5 py-0.5 font-medium text-foreground">
                          {c.label}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => c.folderId && setSelection(c.folderId)}
                          className="rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          {c.label}
                        </button>
                      )}
                    </span>
                  ))}
                </nav>
              );
            })()}
            <div className="flex items-center justify-between">
              {selection !== TRASH_SELECTION ? (
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={includeArchived}
                    onChange={(e) => setIncludeArchived(e.target.checked)}
                  />
                  Show archived
                </label>
              ) : (list.data ?? []).length > 0 ? (
                <p className="text-sm text-muted-foreground">
                  Items here are permanently deleted after 30 days.
                </p>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-2">
                {selection === TRASH_SELECTION && (list.data ?? []).length > 0 && (
                  <button
                    type="button"
                    onClick={() => setEmptyTrashConfirmOpen(true)}
                    className="rounded border border-destructive/40 bg-card px-3 py-1 text-sm text-destructive hover:bg-destructive/10"
                  >
                    Empty Trash
                  </button>
                )}
                <ViewModeToggle mode={viewMode} onChange={setViewMode} />
              </div>
            </div>
            <DocumentsView
              mode={viewMode}
              loading={list.isLoading}
              documents={list.data ?? []}
              folders={folders.data ?? []}
              currentFolderId={
                selection === ROOT_SELECTION || selection === TRASH_SELECTION ? null : selection
              }
              isTrash={selection === TRASH_SELECTION}
              onOpenDocument={(doc) => setViewerDoc(doc)}
              onViewDetails={(doc) => navigate(`/a/arche_vault_documents/${doc.documentId}`)}
              onOpenFolder={(folderId) => setSelection(folderId)}
              onMove={selection === TRASH_SELECTION ? undefined : (doc) => setMovingDoc(doc)}
              onDownload={(doc) => downloadVaultDocument(doc)}
              onSoftDelete={
                selection === TRASH_SELECTION
                  ? undefined
                  : (doc) => softDelete.mutate(doc.documentId)
              }
              onRestore={
                selection === TRASH_SELECTION ? (doc) => restore.mutate(doc.documentId) : undefined
              }
              onPurge={selection === TRASH_SELECTION ? (doc) => setPurgingDoc(doc) : undefined}
              onDropOnFolder={(folderId, documentId) => {
                if (selection === TRASH_SELECTION) return;
                move.mutate({ documentId, targetFolderId: folderId });
              }}
            />
          </div>
        </div>
      )}

      <MoveDocumentDialog
        document={movingDoc}
        folders={folders.data ?? []}
        onClose={() => setMovingDoc(null)}
      />

      <PurgeDocumentDialog
        document={purgingDoc}
        holderId={holderId}
        onClose={() => setPurgingDoc(null)}
      />

      <EmptyTrashDialog
        open={emptyTrashConfirmOpen}
        holderId={holderId}
        trashCount={selection === TRASH_SELECTION ? (list.data ?? []).length : 0}
        onClose={() => setEmptyTrashConfirmOpen(false)}
      />

      <FileViewer
        document={viewerDoc}
        onClose={() => setViewerDoc(null)}
        onDownload={(doc) => downloadVaultDocument(doc)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Folder tree (left rail)
// ---------------------------------------------------------------------------

interface FolderTreeProps {
  holderId: string;
  folders: VaultDocumentFolderDto[];
  loading: boolean;
  selection: Selection;
  onSelect: (selection: Selection) => void;
  /** Drop a dragged document onto a folder row (or "All documents" =
   * Holder root). The dataTransfer payload is the documentId. Trash
   * row intentionally has no drop handler — soft-deleting via drag
   * would be a footgun. */
  onDropDocument?: (folderId: string | null, documentId: string) => void;
}

interface TreeNode {
  folder: VaultDocumentFolderDto;
  children: TreeNode[];
}

/** Assemble the flat folder list into a parent-rooted tree. */
function buildTree(folders: VaultDocumentFolderDto[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  for (const f of folders) byId.set(f.folderId, { folder: f, children: [] });

  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    if (node.folder.parentFolderId && byId.has(node.folder.parentFolderId)) {
      byId.get(node.folder.parentFolderId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const byName = (a: TreeNode, b: TreeNode) =>
    a.folder.name.localeCompare(b.folder.name, undefined, { sensitivity: 'base' });
  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort(byName);
    for (const n of nodes) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

/** UI state for an in-progress mutation against a specific folder.
 * `'create-under'` carries `null` (= Holder root) or a parent folder id;
 * `'rename'` and `'delete'` carry a `vdf_*` id. Exactly one slot is
 * non-null at a time so the row visuals don't double up. */
type FolderMutationUi =
  | { kind: 'create-under'; parentFolderId: string | null }
  | { kind: 'rename'; folderId: string }
  | { kind: 'delete'; folderId: string }
  | null;

function FolderTree({
  holderId,
  folders,
  loading,
  selection,
  onSelect,
  onDropDocument,
}: FolderTreeProps) {
  const tree = useMemo(() => buildTree(folders), [folders]);
  const create = useCreateVaultDocumentFolder();
  const rename = useRenameVaultDocumentFolder();
  const remove = useDeleteVaultDocumentFolder();
  const [ui, setUi] = useState<FolderMutationUi>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const closeUi = () => {
    setUi(null);
    setErrorMsg(null);
  };

  // Use the mutation's own onSuccess/onError callbacks rather than
  // awaiting mutateAsync — that path lets React Query re-render with
  // the post-mutation state before the awaiter resumes, which can
  // leave the inline form mounted for an extra frame and (in some
  // timing windows) appear to not close at all. Attaching the close
  // to the mutation's success path runs it inside the same status
  // transition, with no async gap.
  const handleCreate = (parentFolderId: string | null, name: string) => {
    setErrorMsg(null);
    create.mutate(
      { holderId, parentFolderId, name },
      {
        onSuccess: () => closeUi(),
        onError: (err) => setErrorMsg(friendlyFolderError(err, 'create')),
      },
    );
  };

  const handleRename = (folderId: string, name: string) => {
    setErrorMsg(null);
    rename.mutate(
      { folderId, name },
      {
        onSuccess: () => closeUi(),
        onError: (err) => setErrorMsg(friendlyFolderError(err, 'rename')),
      },
    );
  };

  const handleDelete = (folderId: string, opts?: { force?: boolean }) => {
    setErrorMsg(null);
    remove.mutate(
      { folderId, holderId, force: opts?.force },
      {
        onSuccess: () => {
          // If the deleted folder is currently selected, fall back to
          // "All documents" so the right pane isn't filtering on a
          // vanished id.
          if (selection === folderId) onSelect(ROOT_SELECTION);
          closeUi();
        },
        onError: (err) => setErrorMsg(friendlyFolderError(err, 'delete')),
      },
    );
  };

  const creatingAtRoot = ui?.kind === 'create-under' && ui.parentFolderId === null;

  return (
    <nav className="flex flex-col gap-1 rounded border border-border bg-card p-2 text-sm">
      <TreeRow
        icon={<Inbox className="h-4 w-4" />}
        label="All documents"
        selected={selection === ROOT_SELECTION}
        onClick={() => onSelect(ROOT_SELECTION)}
        depth={0}
        onDropDocument={onDropDocument ? (id) => onDropDocument(null, id) : undefined}
      />

      {!creatingAtRoot && (
        <button
          type="button"
          onClick={() => {
            setErrorMsg(null);
            setUi({ kind: 'create-under', parentFolderId: null });
          }}
          className="flex items-center gap-1.5 rounded px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted/40"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>New folder</span>
        </button>
      )}
      {creatingAtRoot && (
        <InlineFolderNameForm
          placeholder="Folder name"
          busy={create.isPending}
          onSubmit={(name) => handleCreate(null, name)}
          onCancel={closeUi}
          depth={0}
        />
      )}

      {loading && <p className="px-2 py-1 text-xs text-muted-foreground">Loading folders…</p>}

      {tree.map((node) => (
        <FolderNode
          key={node.folder.folderId}
          node={node}
          selection={selection}
          onSelect={onSelect}
          depth={0}
          ui={ui}
          setUi={setUi}
          onCreate={handleCreate}
          onRename={handleRename}
          onDelete={handleDelete}
          createPending={create.isPending}
          renamePending={rename.isPending}
          deletePending={remove.isPending}
          deleteError={errorMsg}
          onDropDocument={onDropDocument}
        />
      ))}

      {errorMsg && (
        <p className="mt-1 rounded bg-destructive/10 px-2 py-1 text-xs text-destructive">
          {errorMsg}
        </p>
      )}

      <div className="my-1 border-t border-border" />

      <TreeRow
        icon={<Trash2 className="h-4 w-4" />}
        label="Trash"
        selected={selection === TRASH_SELECTION}
        onClick={() => onSelect(TRASH_SELECTION)}
        depth={0}
      />
    </nav>
  );
}

interface FolderNodeProps {
  node: TreeNode;
  selection: Selection;
  onSelect: (selection: Selection) => void;
  depth: number;
  ui: FolderMutationUi;
  setUi: (next: FolderMutationUi) => void;
  onCreate: (parentFolderId: string | null, name: string) => void;
  onRename: (folderId: string, name: string) => void;
  onDelete: (folderId: string, opts?: { force?: boolean }) => void;
  createPending: boolean;
  renamePending: boolean;
  deletePending: boolean;
  deleteError: string | null;
  onDropDocument?: (folderId: string | null, documentId: string) => void;
}

function FolderNode({
  node,
  selection,
  onSelect,
  depth,
  ui,
  setUi,
  onCreate,
  onRename,
  onDelete,
  createPending,
  renamePending,
  deletePending,
  deleteError,
  onDropDocument,
}: FolderNodeProps) {
  const selected = selection === node.folder.folderId;
  const Icon = selected ? FolderOpen : FolderIcon;
  const userManaged = !node.folder.isProtected;
  const isRenaming = ui?.kind === 'rename' && ui.folderId === node.folder.folderId;
  const isDeleting = ui?.kind === 'delete' && ui.folderId === node.folder.folderId;
  const isCreatingChild = ui?.kind === 'create-under' && ui.parentFolderId === node.folder.folderId;

  return (
    <>
      {isRenaming ? (
        <InlineFolderNameForm
          initial={node.folder.name}
          busy={renamePending}
          onSubmit={(name) => onRename(node.folder.folderId, name)}
          onCancel={() => setUi(null)}
          depth={depth}
        />
      ) : (
        <TreeRow
          icon={<Icon className="h-4 w-4" />}
          label={node.folder.name}
          protectedFolder={node.folder.isProtected}
          selected={selected}
          onClick={() => onSelect(node.folder.folderId)}
          depth={depth}
          onDropDocument={
            onDropDocument ? (id) => onDropDocument(node.folder.folderId, id) : undefined
          }
          actions={
            userManaged && !isDeleting ? (
              <FolderRowActions
                onNewSubfolder={() =>
                  setUi({ kind: 'create-under', parentFolderId: node.folder.folderId })
                }
                onRename={() => setUi({ kind: 'rename', folderId: node.folder.folderId })}
                onDelete={() => setUi({ kind: 'delete', folderId: node.folder.folderId })}
              />
            ) : null
          }
        />
      )}

      <DeleteFolderDialog
        open={isDeleting}
        folderName={node.folder.name}
        deletePending={deletePending}
        deleteError={deleteError}
        onCancel={() => setUi(null)}
        onDelete={(opts) => onDelete(node.folder.folderId, opts)}
      />

      {isCreatingChild && (
        <InlineFolderNameForm
          placeholder="Subfolder name"
          busy={createPending}
          onSubmit={(name) => onCreate(node.folder.folderId, name)}
          onCancel={() => setUi(null)}
          depth={depth + 1}
        />
      )}

      {node.children.map((child) => (
        <FolderNode
          key={child.folder.folderId}
          node={child}
          selection={selection}
          onSelect={onSelect}
          depth={depth + 1}
          ui={ui}
          setUi={setUi}
          onCreate={onCreate}
          onRename={onRename}
          onDelete={onDelete}
          createPending={createPending}
          renamePending={renamePending}
          deletePending={deletePending}
          deleteError={deleteError}
          onDropDocument={onDropDocument}
        />
      ))}
    </>
  );
}

interface FolderRowActionsProps {
  onNewSubfolder: () => void;
  onRename: () => void;
  onDelete: () => void;
}

function FolderRowActions({ onNewSubfolder, onRename, onDelete }: FolderRowActionsProps) {
  const click = (handler: () => void) => (e: React.MouseEvent) => {
    // Don't fire the parent TreeRow's onClick (would toggle selection)
    // when the user is reaching for an action button.
    e.stopPropagation();
    handler();
  };
  const btn =
    'rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors';
  return (
    <div className="ml-auto flex items-center gap-0.5">
      <button
        type="button"
        aria-label="New subfolder"
        onClick={click(onNewSubfolder)}
        className={btn}
      >
        <Plus className="h-3 w-3" />
      </button>
      <button type="button" aria-label="Rename folder" onClick={click(onRename)} className={btn}>
        <Pencil className="h-3 w-3" />
      </button>
      <button type="button" aria-label="Delete folder" onClick={click(onDelete)} className={btn}>
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

interface InlineFolderNameFormProps {
  initial?: string;
  placeholder?: string;
  busy: boolean;
  onSubmit: (name: string) => void;
  onCancel: () => void;
  depth: number;
}

function InlineFolderNameForm({
  initial,
  placeholder,
  busy,
  onSubmit,
  onCancel,
  depth,
}: InlineFolderNameFormProps) {
  const [value, setValue] = useState(initial ?? '');
  const trimmed = value.trim();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!trimmed || busy) return;
        onSubmit(trimmed);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      className="flex items-center gap-1 px-2 py-1"
      style={{ paddingLeft: `${depth * 12 + 8}px` }}
    >
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder ?? 'Folder name'}
        maxLength={128}
        autoFocus
        className="min-w-0 flex-1 rounded border border-border bg-background px-1.5 py-0.5 text-xs"
      />
      <button
        type="submit"
        disabled={!trimmed || busy}
        className="rounded bg-primary px-2 py-0.5 text-xs text-primary-foreground disabled:opacity-50"
      >
        {busy ? '…' : 'OK'}
      </button>
      <button type="button" onClick={onCancel} className="text-xs text-muted-foreground underline">
        Cancel
      </button>
    </form>
  );
}

/** Turn an op error into copy a user can act on. The folder routes
 * emit 409 `FOLDER_CONFLICT` for non-empty-on-delete and name-clash;
 * 403 `FORBIDDEN` for protected paths and cross-Holder writes. */
function friendlyFolderError(err: unknown, action: 'create' | 'rename' | 'delete'): string {
  if (err instanceof ApiError) {
    if (err.status === 409) {
      return 'A folder with that name already exists at this location.';
    }
    if (err.status === 403) {
      return 'System folders cannot be modified.';
    }
    if (err.status === 400) {
      return err.message || 'Folder name is invalid.';
    }
  }
  return err instanceof Error ? err.message : `Failed to ${action} folder.`;
}

// ---------------------------------------------------------------------------
// Delete folder dialog
//
// Default behavior is non-destructive: the folder and every document
// inside it get soft-deleted into Trash, recoverable for 30 days. The
// dialog is a simple confirm; the previous "force delete" (permanent
// purge) path stays accessible via the API but is intentionally not a
// one-click surface in the UI.
// ---------------------------------------------------------------------------

interface DeleteFolderDialogProps {
  open: boolean;
  folderName: string;
  deletePending: boolean;
  deleteError: string | null;
  onCancel: () => void;
  onDelete: (opts?: { force?: boolean }) => void;
}

function DeleteFolderDialog({
  open,
  folderName,
  deletePending,
  deleteError,
  onCancel,
  onDelete,
}: DeleteFolderDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Move &ldquo;{folderName}&rdquo; to Trash?</DialogTitle>
          <DialogDescription>
            This folder and every document inside it (including subfolders) will be moved to Trash.
            You can restore documents from Trash for up to 30 days.
          </DialogDescription>
        </DialogHeader>

        {deleteError && (
          <p role="alert" className="rounded bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {deleteError}
          </p>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={onCancel}
            disabled={deletePending}
            className="rounded border border-border bg-card px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onDelete()}
            disabled={deletePending}
            className="rounded bg-destructive px-3 py-1.5 text-sm text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
          >
            {deletePending ? 'Moving to Trash…' : 'Move to Trash'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Move-document picker dialog
// ---------------------------------------------------------------------------

interface MoveDocumentDialogProps {
  document: VaultDocumentDto | null;
  folders: VaultDocumentFolderDto[];
  onClose: () => void;
}

function MoveDocumentDialog({ document, folders, onClose }: MoveDocumentDialogProps) {
  const move = useMoveVaultDocument();
  // Stage the target picker selection separately from the doc's current
  // folder so the "Move" button is only enabled when the user actually
  // picks somewhere else. `null` = Holder root.
  const [target, setTarget] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Reset the staged selection whenever a new document opens the dialog.
  useEffect(() => {
    if (document) {
      setTarget(document.folderId ?? null);
      setErrorMsg(null);
    }
  }, [document]);

  const open = document !== null;
  const currentFolderId = document?.folderId ?? null;
  const unchanged = target === currentFolderId;

  // Sort folders by path so /My-Fias/* clusters together, with user
  // folders interleaved alphabetically. Path is already denormalized
  // server-side, so we just compare strings.
  const sorted = useMemo(
    () => [...folders].sort((a, b) => a.path.localeCompare(b.path)),
    [folders],
  );

  const handleMove = async () => {
    if (!document || unchanged) return;
    setErrorMsg(null);
    try {
      await move.mutateAsync({ documentId: document.documentId, targetFolderId: target });
      onClose();
    } catch (err) {
      setErrorMsg(friendlyMoveError(err));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Move document</DialogTitle>
          <DialogDescription>
            Pick a destination folder for "{document?.name}". Protected /My-Fias folders accept
            documents — only their structure is locked.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-72 overflow-y-auto rounded border border-border bg-card">
          <button
            type="button"
            onClick={() => setTarget(null)}
            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
              target === null ? 'bg-primary/10 font-medium' : 'hover:bg-muted/40'
            }`}
          >
            <Inbox className="h-4 w-4 text-muted-foreground" />
            <span>Holder root</span>
            {currentFolderId === null && (
              <span className="ml-auto text-xs text-muted-foreground">current</span>
            )}
          </button>
          {sorted.map((f) => (
            <button
              key={f.folderId}
              type="button"
              onClick={() => setTarget(f.folderId)}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                target === f.folderId ? 'bg-primary/10 font-medium' : 'hover:bg-muted/40'
              }`}
            >
              {f.isProtected ? (
                <Lock className="h-4 w-4 text-muted-foreground" />
              ) : (
                <FolderIcon className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="truncate">{f.path}</span>
              {currentFolderId === f.folderId && (
                <span className="ml-auto text-xs text-muted-foreground">current</span>
              )}
            </button>
          ))}
        </div>

        {errorMsg && (
          <p className="rounded bg-destructive/10 px-3 py-2 text-xs text-destructive">{errorMsg}</p>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/40"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={unchanged || move.isPending}
            onClick={handleMove}
            className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
          >
            {move.isPending ? 'Moving…' : 'Move'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function friendlyMoveError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 422) {
      return 'That folder belongs to a different workspace.';
    }
    if (err.status === 409) {
      return 'This document is in the Trash. Restore it before moving.';
    }
    if (err.status === 403) {
      return "You don't have permission to move this document.";
    }
    if (err.status === 404) {
      return 'Folder not found.';
    }
  }
  return err instanceof Error ? err.message : 'Failed to move document.';
}

// ---------------------------------------------------------------------------
// Permanently-delete one document (Trash row action)
// ---------------------------------------------------------------------------

interface PurgeDocumentDialogProps {
  document: VaultDocumentDto | null;
  holderId: string;
  onClose: () => void;
}

function PurgeDocumentDialog({ document, holderId, onClose }: PurgeDocumentDialogProps) {
  const purge = usePurgeVaultDocument();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const open = document !== null;

  useEffect(() => {
    if (document) setErrorMsg(null);
  }, [document]);

  const handlePurge = async () => {
    if (!document) return;
    setErrorMsg(null);
    try {
      await purge.mutateAsync({ documentId: document.documentId, holderId });
      onClose();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to delete document.');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete permanently</DialogTitle>
          <DialogDescription>
            "{document?.name}" will be permanently removed from the vault. This cannot be undone.
            The audit trail keeps a record of the deletion.
          </DialogDescription>
        </DialogHeader>
        {errorMsg && (
          <p className="rounded bg-destructive/10 px-3 py-2 text-xs text-destructive">{errorMsg}</p>
        )}
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/40"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={purge.isPending}
            onClick={handlePurge}
            className="rounded bg-destructive px-3 py-1.5 text-sm text-destructive-foreground disabled:opacity-50"
          >
            {purge.isPending ? 'Deleting…' : 'Delete permanently'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Empty Trash — loops the batch endpoint until remainingCount === 0
// ---------------------------------------------------------------------------

interface EmptyTrashDialogProps {
  open: boolean;
  holderId: string;
  /** Best-effort count from the in-memory list — purely for the confirm
   * copy. The server is the source of truth for what gets reaped. */
  trashCount: number;
  onClose: () => void;
}

function EmptyTrashDialog({ open, holderId, trashCount, onClose }: EmptyTrashDialogProps) {
  const empty = useEmptyVaultTrash();
  const [running, setRunning] = useState(false);
  const [purgedSoFar, setPurgedSoFar] = useState(0);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [partialFailures, setPartialFailures] = useState<number>(0);

  // Reset transient state when the dialog opens.
  useEffect(() => {
    if (open) {
      setRunning(false);
      setPurgedSoFar(0);
      setRemaining(null);
      setErrorMsg(null);
      setPartialFailures(0);
    }
  }, [open]);

  const runOnce = async () => {
    return empty.mutateAsync(holderId);
  };

  const handleEmpty = async () => {
    setRunning(true);
    setErrorMsg(null);
    let cumulativePurged = 0;
    let cumulativeFailures = 0;
    try {
      // Loop until the server reports `remainingCount === 0` (or only
      // failures remain, which would have us spinning forever — break
      // out as soon as we've made no progress this round).
      while (true) {
        const result = await runOnce();
        cumulativePurged += result.purgedCount;
        cumulativeFailures += result.failures.length;
        setPurgedSoFar(cumulativePurged);
        setPartialFailures(cumulativeFailures);
        setRemaining(result.remainingCount);
        if (result.remainingCount === 0) break;
        if (result.purgedCount === 0) {
          // No progress this call (every row in the batch failed).
          // Stop looping; surface a message.
          setErrorMsg(
            `Some items couldn't be deleted (${cumulativeFailures} failure${
              cumulativeFailures === 1 ? '' : 's'
            }). Try again later.`,
          );
          break;
        }
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to empty trash.');
    } finally {
      setRunning(false);
    }
  };

  const completed = !running && remaining === 0 && !errorMsg;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !running) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Empty Trash</DialogTitle>
          <DialogDescription>
            {completed
              ? `Done. ${purgedSoFar} document${purgedSoFar === 1 ? '' : 's'} permanently deleted${
                  partialFailures > 0
                    ? ` (${partialFailures} couldn't be removed — see logs).`
                    : '.'
                }`
              : running
                ? `Deleting in batches… ${purgedSoFar} purged so far${
                    remaining != null && remaining > 0 ? `, ${remaining} remaining.` : '.'
                  }`
                : `Permanently delete ${
                    trashCount > 0
                      ? `the ${trashCount} document${trashCount === 1 ? '' : 's'}`
                      : 'every document'
                  } in this workspace's Trash. This cannot be undone.`}
          </DialogDescription>
        </DialogHeader>
        {errorMsg && (
          <p className="rounded bg-destructive/10 px-3 py-2 text-xs text-destructive">{errorMsg}</p>
        )}
        <DialogFooter>
          <button
            type="button"
            disabled={running}
            onClick={onClose}
            className="rounded border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/40 disabled:opacity-50"
          >
            {completed ? 'Close' : 'Cancel'}
          </button>
          {!completed && (
            <button
              type="button"
              disabled={running}
              onClick={handleEmpty}
              className="rounded bg-destructive px-3 py-1.5 text-sm text-destructive-foreground disabled:opacity-50"
            >
              {running ? 'Deleting…' : 'Empty Trash'}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface TreeRowProps {
  icon: React.ReactNode;
  label: string;
  protectedFolder?: boolean;
  selected: boolean;
  onClick: () => void;
  depth: number;
  /** Right-aligned slot for hover actions. Only user-managed folders
   * pass anything here — protected folders keep the lock badge. */
  actions?: React.ReactNode;
  /** When provided, the row becomes a drop target. Fires with the
   * documentId carried in `dataTransfer`. */
  onDropDocument?: (documentId: string) => void;
}

function TreeRow({
  icon,
  label,
  protectedFolder,
  selected,
  onClick,
  depth,
  actions,
  onDropDocument,
}: TreeRowProps) {
  const [dropHover, setDropHover] = useState(false);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      onDragOver={
        onDropDocument
          ? (e) => {
              const types = e.dataTransfer.types;
              if (!types.includes(DRAG_DOC_MIME)) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              setDropHover(true);
            }
          : undefined
      }
      onDragLeave={onDropDocument ? () => setDropHover(false) : undefined}
      onDrop={
        onDropDocument
          ? (e) => {
              e.preventDefault();
              setDropHover(false);
              const documentId = e.dataTransfer.getData(DRAG_DOC_MIME);
              if (documentId) onDropDocument(documentId);
            }
          : undefined
      }
      className={`group flex items-center gap-2 rounded px-2 py-1 text-left text-sm transition ${
        selected
          ? 'bg-primary/10 font-medium text-foreground'
          : 'text-muted-foreground hover:bg-muted/40'
      } ${dropHover ? 'ring-2 ring-primary/60 bg-primary/5' : ''}`}
      style={{ paddingLeft: `${depth * 12 + 8}px` }}
    >
      {icon}
      <span className="truncate">{label}</span>
      {protectedFolder ? (
        <Lock className="ml-auto h-3 w-3 text-muted-foreground" aria-label="System folder" />
      ) : (
        actions
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Search results
// ---------------------------------------------------------------------------

interface SearchResultsProps {
  query: string;
  loading: boolean;
  matches:
    | Array<{
        documentId: string;
        documentName: string;
        chunkIndex: number;
        similarity: number;
        content: string | null;
      }>
    | undefined;
  onOpen: (documentId: string) => void;
}

function SearchResults({ query, loading, matches, onOpen }: SearchResultsProps) {
  if (loading) {
    return <p className="text-sm text-muted-foreground">Searching…</p>;
  }
  if (!matches || matches.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No matches for <span className="font-medium text-foreground">{query}</span>.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted-foreground">
        {matches.length} match{matches.length === 1 ? '' : 'es'} for{' '}
        <span className="font-medium text-foreground">{query}</span>:
      </p>
      <ul className="space-y-2">
        {matches.map((m) => (
          <li
            key={m.documentId + m.chunkIndex}
            className="cursor-pointer rounded border border-border bg-card p-3 hover:bg-muted/40"
            onClick={() => onOpen(m.documentId)}
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-foreground">{m.documentName}</span>
              <span className="text-xs text-muted-foreground">
                {(m.similarity * 100).toFixed(0)}% match · chunk {m.chunkIndex + 1}
              </span>
            </div>
            {m.content && (
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{m.content}</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Documents view (table OR grid)
// ---------------------------------------------------------------------------

/** Custom MIME type for HTML5 drag-and-drop. The dataTransfer carries the
 * documentId being dragged; folder rows + grid tiles register as drop
 * targets via the same MIME so we don't accidentally accept native file
 * drags from the OS. */
export const DRAG_DOC_MIME = 'application/x-fias-vault-document-id';

export function isImageMime(mime: string | null | undefined): boolean {
  return typeof mime === 'string' && mime.startsWith('image/');
}

/** Hit `/vault/documents/:id/download` for a fresh signed URL, then
 * synthesize a transient `<a download>` click so the browser saves the
 * file under its real name. The signed URL already carries
 * `Content-Disposition: attachment`, but `window.open` opens a tab
 * instead of saving — the anchor trick keeps the user on the page. */
export async function downloadVaultDocument(doc: VaultDocumentDto): Promise<void> {
  try {
    const { url } = await api.getVaultDocumentDownloadUrl(doc.documentId);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = doc.name;
    a.rel = 'noopener';
    a.target = '_blank';
    window.document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (err) {
    // Swallow — the UI doesn't surface errors for this verb yet. A
    // future polish pass can wire a toast.
    console.error('Vault document download failed', err);
  }
}

interface DocumentsViewProps {
  mode: 'list' | 'grid';
  loading: boolean;
  documents: VaultDocumentDto[];
  /** All folders for the active Holder. The grid uses these to render
   * sibling folder tiles alongside documents (Explorer-style). */
  folders: VaultDocumentFolderDto[];
  /** Currently-selected folder id, or `null` for Holder root. Used by
   * the grid to filter which folders are direct children. */
  currentFolderId: string | null;
  isTrash: boolean;
  /** Default click — opens the inline preview/viewer for any file type. */
  onOpenDocument: (document: VaultDocumentDto) => void;
  /** Secondary action — navigate to the full DocumentDetailPage. Surfaced
   * as an `Info` button in RowActions so users who want metadata
   * (extracted text preview, share panel, version chain) can still
   * get there. */
  onViewDetails: (document: VaultDocumentDto) => void;
  onOpenFolder: (folderId: string) => void;
  onMove?: (document: VaultDocumentDto) => void;
  onDownload: (document: VaultDocumentDto) => void;
  onSoftDelete?: (document: VaultDocumentDto) => void;
  onRestore?: (document: VaultDocumentDto) => void;
  onPurge?: (document: VaultDocumentDto) => void;
  /** Drop a dragged document onto a sibling folder tile (grid view).
   * The folder tree handles its own drop targets via FolderTree. */
  onDropOnFolder?: (folderId: string, documentId: string) => void;
}

export function DocumentsView(props: DocumentsViewProps) {
  if (props.loading) {
    return <p className="text-sm text-muted-foreground">Loading documents…</p>;
  }
  // Both views surface direct child folders of the current selection
  // (Windows-Explorer convention) — except in the Trash view, which is
  // a folder-agnostic flat list of purge candidates.
  const childFolders = !props.isTrash
    ? props.folders.filter((f) => (f.parentFolderId ?? null) === props.currentFolderId)
    : [];
  const empty = props.documents.length === 0 && childFolders.length === 0;
  if (empty) {
    return (
      <div className="rounded border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          {props.isTrash
            ? 'Trash is empty.'
            : 'No documents here. Drag-and-drop a file above to get started.'}
        </p>
      </div>
    );
  }
  if (props.mode === 'list') return <DocumentsTable {...props} childFolders={childFolders} />;
  return <DocumentsGrid {...props} childFolders={childFolders} />;
}

// ---------------------------------------------------------------------------
// Table view
// ---------------------------------------------------------------------------

function DocumentsTable({
  documents,
  isTrash,
  childFolders,
  onOpenDocument,
  onOpenFolder,
  onViewDetails,
  onMove,
  onDownload,
  onSoftDelete,
  onRestore,
  onPurge,
  onDropOnFolder,
}: DocumentsViewProps & { childFolders: VaultDocumentFolderDto[] }) {
  return (
    <div className="overflow-x-auto rounded border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40 text-left text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Name</th>
            <th className="px-3 py-2 font-medium">Sensitivity</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Size</th>
            <th className="px-3 py-2 font-medium">{isTrash ? 'Deleted' : 'Updated'}</th>
            <th className="px-3 py-2 font-medium" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {childFolders.map((f) => (
            <FolderTableRow
              key={f.folderId}
              folder={f}
              onOpen={() => onOpenFolder(f.folderId)}
              onDropDocument={onDropOnFolder ? (id) => onDropOnFolder(f.folderId, id) : undefined}
            />
          ))}
          {documents.map((d) => (
            <tr
              key={d.documentId}
              draggable={!isTrash}
              onDragStart={
                isTrash
                  ? undefined
                  : (e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData(DRAG_DOC_MIME, d.documentId);
                    }
              }
              className="cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/30"
              onClick={() => onOpenDocument(d)}
            >
              <td className="px-3 py-2 text-foreground">
                <div className="flex flex-col">
                  <span className="truncate font-medium">{d.name}</span>
                  {d.tags.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {d.tags.slice(0, 4).join(', ')}
                      {d.tags.length > 4 ? '…' : ''}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2">
                <SensitivityBadge sensitivity={d.sensitivity} />
              </td>
              <td className="px-3 py-2 text-muted-foreground">{d.status}</td>
              <td className="px-3 py-2 text-muted-foreground">{formatBytes(d.sizeBytes)}</td>
              <td className="px-3 py-2 text-muted-foreground">
                {new Date(
                  isTrash ? (d.deletedAt ?? d.updatedAt) : d.updatedAt,
                ).toLocaleDateString()}
              </td>
              <td className="px-3 py-2 text-right">
                <RowActions
                  document={d}
                  onViewDetails={onViewDetails}
                  onMove={onMove}
                  onDownload={onDownload}
                  onSoftDelete={onSoftDelete}
                  onRestore={onRestore}
                  onPurge={onPurge}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Folder row in the table view. Renders with the same column layout
 * as a document row so the table doesn't visually fracture, but the
 * empty cells make the difference obvious. Click navigates into the
 * folder (same as the grid's FolderTile); folder management lives in
 * the left rail. Drop target for the same documentId drag-and-drop. */
interface FolderTableRowProps {
  folder: VaultDocumentFolderDto;
  onOpen: () => void;
  onDropDocument?: (documentId: string) => void;
}

export function FolderTableRow({ folder, onOpen, onDropDocument }: FolderTableRowProps) {
  const [dropHover, setDropHover] = useState(false);
  return (
    <tr
      onClick={onOpen}
      onDragOver={
        onDropDocument
          ? (e) => {
              if (!e.dataTransfer.types.includes(DRAG_DOC_MIME)) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              setDropHover(true);
            }
          : undefined
      }
      onDragLeave={onDropDocument ? () => setDropHover(false) : undefined}
      onDrop={
        onDropDocument
          ? (e) => {
              e.preventDefault();
              setDropHover(false);
              const id = e.dataTransfer.getData(DRAG_DOC_MIME);
              if (id) onDropDocument(id);
            }
          : undefined
      }
      className={`cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/30 ${
        dropHover ? 'bg-primary/5 ring-1 ring-inset ring-primary/40' : ''
      }`}
    >
      <td className="px-3 py-2 text-foreground">
        <div className="flex items-center gap-2">
          <FolderIcon
            className={`h-4 w-4 shrink-0 ${
              folder.isProtected ? 'text-muted-foreground' : 'text-primary/70'
            }`}
          />
          <span className="truncate font-medium">{folder.name}</span>
          {folder.isProtected && (
            <Lock className="h-3 w-3 text-muted-foreground" aria-label="System folder" />
          )}
        </div>
      </td>
      <td className="px-3 py-2" />
      <td className="px-3 py-2 text-muted-foreground">Folder</td>
      <td className="px-3 py-2" />
      <td className="px-3 py-2" />
      <td className="px-3 py-2" />
    </tr>
  );
}

interface RowActionsProps {
  document: VaultDocumentDto;
  onViewDetails?: (document: VaultDocumentDto) => void;
  onMove?: (document: VaultDocumentDto) => void;
  onDownload: (document: VaultDocumentDto) => void;
  onSoftDelete?: (document: VaultDocumentDto) => void;
  onRestore?: (document: VaultDocumentDto) => void;
  onPurge?: (document: VaultDocumentDto) => void;
}

export function RowActions({
  document: doc,
  onViewDetails,
  onMove,
  onDownload,
  onSoftDelete,
  onRestore,
  onPurge,
}: RowActionsProps) {
  const stop = (handler?: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handler?.();
  };
  const btn =
    'rounded border border-border bg-card p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors';
  const dangerBtn =
    'rounded border border-destructive/40 bg-card p-1 text-destructive hover:bg-destructive/10 transition-colors';
  return (
    <div className="flex items-center justify-end gap-1">
      {onViewDetails && (
        <button
          type="button"
          aria-label="View details"
          title="View details"
          onClick={stop(() => onViewDetails(doc))}
          className={btn}
        >
          <Info className="h-4 w-4" />
        </button>
      )}
      <button
        type="button"
        aria-label="Download"
        title="Download"
        onClick={stop(() => onDownload(doc))}
        className={btn}
      >
        <DownloadIcon className="h-4 w-4" />
      </button>
      {onMove && (
        <button
          type="button"
          aria-label="Move to folder"
          title="Move to folder"
          onClick={stop(() => onMove(doc))}
          className={btn}
        >
          <ArrowRightLeft className="h-4 w-4" />
        </button>
      )}
      {onRestore && (
        <button
          type="button"
          aria-label="Restore"
          title="Restore from trash"
          onClick={stop(() => onRestore(doc))}
          className={btn}
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      )}
      {onSoftDelete && (
        <button
          type="button"
          aria-label="Move to trash"
          title="Move to trash"
          onClick={stop(() => onSoftDelete(doc))}
          className={dangerBtn}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      {onPurge && (
        <button
          type="button"
          aria-label="Delete permanently"
          title="Delete permanently"
          onClick={stop(() => onPurge(doc))}
          className={dangerBtn}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grid view — Windows Explorer style tiles
// ---------------------------------------------------------------------------

interface DocumentsGridProps extends DocumentsViewProps {
  childFolders: VaultDocumentFolderDto[];
}

function DocumentsGrid({
  documents,
  isTrash,
  childFolders,
  onOpenDocument,
  onViewDetails,
  onOpenFolder,
  onMove,
  onDownload,
  onSoftDelete,
  onRestore,
  onPurge,
  onDropOnFolder,
}: DocumentsGridProps) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {childFolders.map((f) => (
        <FolderTile
          key={f.folderId}
          folder={f}
          onOpen={() => onOpenFolder(f.folderId)}
          onDropDocument={onDropOnFolder ? (id) => onDropOnFolder(f.folderId, id) : undefined}
        />
      ))}
      {documents.map((d) => (
        <DocumentTile
          key={d.documentId}
          document={d}
          isTrash={isTrash}
          onOpen={() => onOpenDocument(d)}
          onViewDetails={onViewDetails}
          onMove={onMove}
          onDownload={onDownload}
          onSoftDelete={onSoftDelete}
          onRestore={onRestore}
          onPurge={onPurge}
        />
      ))}
    </div>
  );
}

interface FolderTileProps {
  folder: VaultDocumentFolderDto;
  onOpen: () => void;
  onDropDocument?: (documentId: string) => void;
}

function FolderTile({ folder, onOpen, onDropDocument }: FolderTileProps) {
  const [dropHover, setDropHover] = useState(false);
  return (
    <button
      type="button"
      onDoubleClick={onOpen}
      onClick={onOpen}
      onDragOver={
        onDropDocument
          ? (e) => {
              if (!e.dataTransfer.types.includes(DRAG_DOC_MIME)) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              setDropHover(true);
            }
          : undefined
      }
      onDragLeave={onDropDocument ? () => setDropHover(false) : undefined}
      onDrop={
        onDropDocument
          ? (e) => {
              e.preventDefault();
              setDropHover(false);
              const id = e.dataTransfer.getData(DRAG_DOC_MIME);
              if (id) onDropDocument(id);
            }
          : undefined
      }
      className={`flex flex-col items-center gap-2 rounded border border-border bg-card p-4 text-center transition hover:border-primary/40 hover:bg-muted/30 ${
        dropHover ? 'ring-2 ring-primary/60 bg-primary/5' : ''
      }`}
    >
      <FolderIcon
        className={`h-12 w-12 ${folder.isProtected ? 'text-muted-foreground' : 'text-primary/70'}`}
      />
      <span className="line-clamp-2 break-words text-xs font-medium text-foreground">
        {folder.name}
      </span>
      {folder.isProtected && (
        <Lock className="h-3 w-3 text-muted-foreground" aria-label="System folder" />
      )}
    </button>
  );
}

interface DocumentTileProps {
  document: VaultDocumentDto;
  isTrash: boolean;
  onOpen: () => void;
  onViewDetails?: (document: VaultDocumentDto) => void;
  onMove?: (document: VaultDocumentDto) => void;
  onDownload: (document: VaultDocumentDto) => void;
  onSoftDelete?: (document: VaultDocumentDto) => void;
  onRestore?: (document: VaultDocumentDto) => void;
  onPurge?: (document: VaultDocumentDto) => void;
}

function DocumentTile({
  document: doc,
  isTrash,
  onOpen,
  onViewDetails,
  onMove,
  onDownload,
  onSoftDelete,
  onRestore,
  onPurge,
}: DocumentTileProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      draggable={!isTrash}
      onDragStart={
        isTrash
          ? undefined
          : (e) => {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData(DRAG_DOC_MIME, doc.documentId);
            }
      }
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group flex cursor-pointer flex-col gap-2 rounded border border-border bg-card p-2 transition hover:border-primary/40 hover:shadow-sm"
    >
      <div className="relative flex h-32 items-center justify-center overflow-hidden rounded bg-muted/40">
        {isImageMime(doc.mimeType) ? (
          <DocumentThumbnail documentId={doc.documentId} name={doc.name} />
        ) : (
          <FileIcon className="h-12 w-12 text-muted-foreground" />
        )}
      </div>
      <div className="flex min-h-[2.5rem] flex-col">
        <span className="line-clamp-2 break-words text-xs font-medium text-foreground">
          {doc.name}
        </span>
        <span className="mt-0.5 text-[10px] text-muted-foreground">
          {formatBytes(doc.sizeBytes)}
        </span>
      </div>
      <div className="opacity-0 transition group-hover:opacity-100">
        <RowActions
          document={doc}
          onViewDetails={onViewDetails}
          onMove={onMove}
          onDownload={onDownload}
          onSoftDelete={onSoftDelete}
          onRestore={onRestore}
          onPurge={onPurge}
        />
      </div>
    </div>
  );
}

/** Lazily fetches a signed download URL on mount, uses it as the
 * `<img>` src. Cached per-mount; signed URL TTL is ~1 hour. Re-fetches
 * if the documentId prop changes (different doc) or the component
 * remounts (new tile pass). */
function DocumentThumbnail({ documentId, name }: { documentId: string; name: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setErrored(false);
    setUrl(null);
    api
      .getVaultDocumentDownloadUrl(documentId)
      .then((res) => {
        if (!cancelled) setUrl(res.url);
      })
      .catch(() => {
        if (!cancelled) setErrored(true);
      });
    return () => {
      cancelled = true;
    };
  }, [documentId]);
  if (errored) return <FileIcon className="h-12 w-12 text-muted-foreground" />;
  if (!url) return <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />;
  return (
    <img
      src={url}
      alt={name}
      loading="lazy"
      draggable={false}
      className="h-full w-full object-cover"
      onError={() => setErrored(true)}
    />
  );
}

// ---------------------------------------------------------------------------
// View-mode toggle
// ---------------------------------------------------------------------------

export function ViewModeToggle({
  mode,
  onChange,
}: {
  mode: 'list' | 'grid';
  onChange: (mode: 'list' | 'grid') => void;
}) {
  const base =
    'rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors';
  const active = 'bg-muted text-foreground';
  return (
    <div className="flex items-center rounded border border-border bg-card p-0.5">
      <button
        type="button"
        aria-label="Grid view"
        title="Grid view"
        onClick={() => onChange('grid')}
        className={`${base} ${mode === 'grid' ? active : ''}`}
      >
        <LayoutGrid className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="List view"
        title="List view"
        onClick={() => onChange('list')}
        className={`${base} ${mode === 'list' ? active : ''}`}
      >
        <ListIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// File viewer modal — image / pdf / video / audio / text / fallback.
// Ported from the legacy FilePreview (deleted in 8ff1ac2b), adapted to
// the Vault Documents data shape.
// ---------------------------------------------------------------------------

type PreviewType = 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'unsupported';

export function previewTypeFor(mime: string | null | undefined): PreviewType {
  if (!mime) return 'unsupported';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  if (
    mime.startsWith('text/') ||
    mime === 'application/json' ||
    mime === 'application/xml' ||
    mime === 'application/javascript'
  ) {
    return 'text';
  }
  return 'unsupported';
}

interface FileViewerProps {
  document: VaultDocumentDto | null;
  onClose: () => void;
  onDownload: (document: VaultDocumentDto) => void;
}

export function FileViewer({ document: doc, onClose, onDownload }: FileViewerProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  const previewType = previewTypeFor(doc?.mimeType ?? null);

  // Fetch a signed URL (and text content for text previews) whenever
  // the viewer opens on a different doc. Reset zoom/rotation so the
  // user starts from a known state.
  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUrl(null);
    setTextContent(null);
    setZoom(1);
    setRotation(0);
    (async () => {
      try {
        const res = await api.getVaultDocumentDownloadUrl(doc.documentId);
        if (cancelled) return;
        setUrl(res.url);
        if (previewType === 'text') {
          // Fetch up to ~100KB and render as monospace. Truncation
          // matches the legacy preview behavior. Network failure on
          // this fetch falls through to the unsupported branch via
          // the catch below.
          const textRes = await fetch(res.url);
          if (cancelled) return;
          const raw = await textRes.text();
          setTextContent(raw.slice(0, 100_000));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load preview');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doc, previewType]);

  // Esc closes; +/- zoom; 0 resets. Only meaningful for image preview
  // but harmless otherwise (the keys don't conflict with native
  // controls on video/audio/iframe).
  useEffect(() => {
    if (!doc) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (previewType === 'image') {
        if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(z + 0.25, 5));
        else if (e.key === '-') setZoom((z) => Math.max(z - 0.25, 0.25));
        else if (e.key === '0') {
          setZoom(1);
          setRotation(0);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [doc, onClose, previewType]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!doc) return;
    const prev = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.document.body.style.overflow = prev;
    };
  }, [doc]);

  if (!doc) return null;

  const showImageControls = previewType === 'image';

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <h2 className="truncate text-lg font-medium text-foreground">{doc.name}</h2>
          <span className="text-sm text-muted-foreground">{formatBytes(doc.sizeBytes)}</span>
        </div>
        <div className="flex items-center gap-2">
          {showImageControls && (
            <>
              <button
                type="button"
                aria-label="Zoom out"
                title="Zoom out"
                onClick={() => setZoom((z) => Math.max(z - 0.25, 0.25))}
                className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="w-12 text-center text-sm text-muted-foreground">
                {Math.round(zoom * 100)}%
              </span>
              <button
                type="button"
                aria-label="Zoom in"
                title="Zoom in"
                onClick={() => setZoom((z) => Math.min(z + 0.25, 5))}
                className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Rotate"
                title="Rotate"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
              >
                <RotateCw className="h-4 w-4" />
              </button>
              <div className="mx-1 h-6 w-px bg-border" />
            </>
          )}
          <button
            type="button"
            aria-label="Download"
            title="Download"
            onClick={() => onDownload(doc)}
            className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
          >
            <DownloadIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Close"
            title="Close (Esc)"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 items-center justify-center overflow-hidden p-4">
        {loading ? (
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        ) : error ? (
          <UnsupportedPreviewFallback
            name={doc.name}
            error={error}
            onDownload={() => onDownload(doc)}
          />
        ) : url ? (
          <PreviewContent
            type={previewType}
            url={url}
            name={doc.name}
            textContent={textContent}
            zoom={zoom}
            rotation={rotation}
            onDownload={() => onDownload(doc)}
          />
        ) : null}
      </div>
    </div>
  );
}

interface PreviewContentProps {
  type: PreviewType;
  url: string;
  name: string;
  textContent: string | null;
  zoom: number;
  rotation: number;
  onDownload: () => void;
}

function PreviewContent({
  type,
  url,
  name,
  textContent,
  zoom,
  rotation,
  onDownload,
}: PreviewContentProps) {
  switch (type) {
    case 'image':
      return (
        <img
          src={url}
          alt={name}
          draggable={false}
          className="max-h-full max-w-full select-none object-contain"
          style={{
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            transition: 'transform 80ms ease-out',
          }}
        />
      );
    case 'video':
      return (
        <video src={url} controls className="max-h-full max-w-full">
          Your browser does not support the video tag.
        </video>
      );
    case 'audio':
      return (
        <div className="flex flex-col items-center gap-4">
          <FileIcon className="h-24 w-24 text-muted-foreground" />
          <p className="text-base font-medium text-foreground">{name}</p>
          <audio src={url} controls className="w-full max-w-md">
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    case 'pdf':
      return <iframe src={url} title={name} className="h-full w-full border-0" />;
    case 'text':
      return (
        <div className="h-full w-full max-w-4xl overflow-auto">
          <pre className="whitespace-pre-wrap rounded-lg bg-muted p-4 font-mono text-sm text-foreground">
            {textContent ?? ''}
          </pre>
        </div>
      );
    case 'unsupported':
    default:
      return <UnsupportedPreviewFallback name={name} onDownload={onDownload} />;
  }
}

function UnsupportedPreviewFallback({
  name,
  error,
  onDownload,
}: {
  name: string;
  error?: string;
  onDownload: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 text-center text-sm text-muted-foreground">
      {error ? (
        <AlertCircle className="h-10 w-10 text-destructive" />
      ) : (
        <FileIcon className="h-16 w-16 text-muted-foreground" />
      )}
      <p className="text-base font-medium text-foreground">{name}</p>
      <p>{error ? 'Failed to load preview' : 'Preview not available for this file type.'}</p>
      {error && <p className="text-xs">{error}</p>}
      <button
        type="button"
        onClick={onDownload}
        className="mt-1 inline-flex items-center gap-2 rounded border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted/40"
      >
        <DownloadIcon className="h-4 w-4" />
        Download
      </button>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
